/**
 * @module cart-page
 * @description Full cart page entry point — loaded on /cart template only.
 *
 * Uses Section Rendering API + morph for seamless quantity updates
 * without full-page reload. Listens to CART_EVENTS for cross-component sync.
 *
 * @see {@link @stardust/cart} — core cart state + Shopify API
 * @see {@link @stardust/section-renderer} — Section Rendering API wrapper
 */
import { morphSection, sectionRenderer } from "@stardust/section-renderer";
import { CART_EVENTS, announceCartStatus, guardQuantityEnter, setRowsLocked, startRowRemoval } from "@stardust/cart";
import { changeLine } from "@stardust/cart-api";
import { formatMoney } from "@stardust/utilities";

/** Ignore cart:updated echoes of our own update for this long after it lands. */
const OWN_UPDATE_ECHO_MS = 2000;

class CartPage {
  #isUpdating = false;
  #lastOwnUpdate = 0;
  #rerenderQueued = false;
  /** A cart:refresh-drawer arrived mid-update; re-render once the update lands. */
  #refreshSkippedDuringUpdate = false;
  /** Bumped by every lock and re-render, so only the newest re-render may unlock the rows. */
  #lockGeneration = 0;
  /** @type {boolean} */
  #rehydratingFromHistory = false;
  /** @type {((e: PageTransitionEvent) => void) | null} */
  #onPageShow = null;

  constructor() {
    this.sectionEl = document.querySelector('[data-section-type="cart"]');
    if (!this.sectionEl) return;

    this.sectionId = this.sectionEl.dataset.sectionId;
    this.wrapper = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!this.wrapper) return;

    this.initSection();
    this.bindEvents();
    this.#bindHistoryRestoration();
    guardQuantityEnter();
  }

  initSection() {
    this.initAffirm();
  }

  initAffirm() {
    const wrapper = this.wrapper?.querySelector("[data-affirm-wrapper]");
    if (!wrapper) return;
    const p = wrapper.querySelector(".affirm-as-low-as");
    if (!p) return;

    if (p.textContent.trim() || window.affirm) {
      wrapper.hidden = false;
      return;
    }

    const observer = new MutationObserver(() => {
      if (p.textContent.trim()) {
        wrapper.hidden = false;
        observer.disconnect();
      }
    });
    observer.observe(p, { childList: true, characterData: true, subtree: true });
  }

  bindEvents() {
    // Handle explicit remove button clicks
    this.wrapper.addEventListener("click", (e) => {
      const removeBtn = e.target.closest("[data-item-remove]");
      if (removeBtn) {
        const cartItem = removeBtn.closest("[data-cart-item]");
        if (cartItem && !this.#isUpdating) this.removeItem(cartItem);
      }
    });

    // Handle remove-at-qty-1 from quantity spinner — intercepts before Cart class
    this.wrapper.addEventListener("cart:remove-item", (e) => {
      e.stopPropagation(); // Prevent Cart class (cart drawer) from also handling
      const cartItem = e.target.closest("[data-cart-item]");
      if (cartItem && !this.#isUpdating) this.removeItem(cartItem);
    });

    // Sent immediately, no debounce, same as the drawer: requests target rows by position, and one
    // change can shift every row after it (a removal, or a discount re-splitting a line), so every
    // row locks the moment one changes and stays locked until the re-render lands.
    this.wrapper.addEventListener("change", (e) => {
      if (!e.target.matches("[data-quantity-spinner-input]")) return;
      const cartItem = e.target.closest("[data-cart-item]");
      if (!cartItem || this.#isUpdating) return;

      const line = parseInt(cartItem.dataset.line, 10);
      const quantity = parseInt(e.target.value, 10);
      if (!isNaN(line) && !isNaN(quantity)) {
        this.updateQuantity(line, quantity, { title: cartItem.dataset.itemTitle });
      }
    });

    document.addEventListener(CART_EVENTS.updated, (e) => {
      if (this.#isUpdating) return;
      if (Date.now() - this.#lastOwnUpdate < OWN_UPDATE_ECHO_MS) return;
      if (e.detail) {
        this.updateCartState(e.detail);
        this.updateDiscounts(e.detail.cart_level_discount_applications);
        this.updateShipping(e.detail.items_subtotal_price);
        this.updateFinancing(e.detail.total_price);
      }
      this.#queueRerender();
    });

    // A discount code applied or removed (cart-discount.js) hands the new cart to the drawer
    // without a cart:updated, so the page re-renders its own totals and code list here.
    // Mid-update the quantity morph would race it, so it waits for the update to finish.
    document.addEventListener(CART_EVENTS.refreshDrawer, () => {
      if (this.#isUpdating) {
        this.#refreshSkippedDuringUpdate = true;
        return;
      }
      this.#queueRerender();
    });

    // A discount write or an add can re-split lines, so the page's rows lock for the whole write,
    // the same as the drawer's: a quantity change sent meanwhile would use stale row positions.
    // Every add-start is followed by add-failed or a refresh, and the re-render unlocks.
    document.addEventListener(CART_EVENTS.addStart, () => {
      this.#lockGeneration++;
      if (!this.#isUpdating) this.setLoading(true);
    });
    document.addEventListener(CART_EVENTS.addFailed, () => {
      if (!this.#isUpdating) this.setLoading(false);
    });

    this.wrapper.addEventListener("theme:section:morph", () => {
      this.#refreshSectionRef();
    });
  }

  /**
   * After Shop Pay (or other wallet) redirect, browser back/forward and bfcache
   * can leave `content_for_additional_checkout_buttons` iframes empty. Re-fetch
   * the cart section so Shopify can re-inject Amazon Pay, Shop Pay, etc.
   */
  #bindHistoryRestoration() {
    this.#onPageShow = (e) => {
      if (this.#rehydratingFromHistory) return;
      const nav = performance.getEntriesByType("navigation")[0];
      const isBackForward =
        nav && "type" in nav && /** @type {PerformanceNavigationTiming} */ (nav).type === "back_forward";
      if (!e.persisted && !isBackForward) return;
      void this.#refreshCartSectionAfterHistoryNavigation();
    };
    window.addEventListener("pageshow", this.#onPageShow);
  }

  /**
   * @returns {Promise<void>}
   */
  async #refreshCartSectionAfterHistoryNavigation() {
    if (!this.sectionId) return;
    this.#rehydratingFromHistory = true;
    await this.#rerenderSection();
    this.#rehydratingFromHistory = false;
  }

  /**
   * Re-render the cart section from the server's current cart.
   * @returns {Promise<void>}
   */
  async #rerenderSection() {
    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#refreshSectionRef();
    } catch (err) {
      console.warn("[CartPage] Failed to re-render cart section:", err);
    }
  }

  /**
   * Re-render once for events that arrive together: an add fires cart:updated and
   * cart:refresh-drawer back to back, which would otherwise be two section requests.
   * Releases the add-start lock once the fresh rows are in, but only if no newer write or
   * re-render started meanwhile: the renderer aborts the older request, and a write still
   * open must keep the rows locked.
   */
  #queueRerender() {
    if (this.#rerenderQueued) return;
    this.#rerenderQueued = true;
    queueMicrotask(async () => {
      this.#rerenderQueued = false;
      const generation = ++this.#lockGeneration;
      await this.#rerenderSection();
      if (generation === this.#lockGeneration && !this.#isUpdating) this.setLoading(false);
    });
  }

  #refreshSectionRef() {
    this.sectionEl = document.querySelector('[data-section-type="cart"]');
    if (!this.sectionEl) return;
    this.initSection();
  }

  /**
   * @param {number} line - 1-based position in cart.items
   * @param {number} quantity - 0 removes the line
   * @param {Object} [options]
   * @param {string} [options.title] - product title, for the announcement
   * @param {{ settled: Promise<void>, cancel: () => void }|null} [options.removal] - from startRowRemoval
   * @returns {Promise<boolean>} whether the update went through
   */
  async updateQuantity(line, quantity, { title = "", removal = null } = {}) {
    this.#isUpdating = true;
    this.setLoading(true);
    let ok = false;

    try {
      const data = await changeLine(line, quantity, {
        sections: this.sectionId,
        sectionsUrl: window.location.pathname,
      });

      if (data.errors) {
        console.error("Cart error:", data.errors);
      } else {
        // Let the collapse finish, then drop the row. Rows have no id, so morph matches them by
        // position: a collapsed row left in place would be reused for the next item and flash
        // back open.
        if (removal) {
          await removal.settled;
          for (const row of this.wrapper.querySelectorAll("[data-removing]")) row.remove();
        }
        const sectionHTML = data.sections?.[this.sectionId];
        if (sectionHTML) {
          morphSection(this.sectionId, sectionHTML);
        } else {
          // Shopify sends null when a section fails to render. The change landed, so the rows'
          // positions are stale until something re-renders them; do it before unlocking.
          await this.#rerenderSection();
        }

        this.updateCartBadges(data.item_count);
        this.updateCartState(data);
        this.updateDiscounts(data.cart_level_discount_applications);
        this.updateShipping(data.items_subtotal_price);
        this.updateFinancing(data.total_price);
        this.#announce(quantity === 0 ? "Removed" : "Updated", { title, quantity });
        this.#lastOwnUpdate = Date.now();
        document.dispatchEvent(new CustomEvent(CART_EVENTS.updated, { detail: data }));
        ok = true;
      }
    } catch (error) {
      console.error("Cart update failed:", error);
    }

    // On failure the inputs still show the quantity the shopper asked for, and the request may
    // have landed anyway with its response lost. Re-render from the server's cart before
    // unlocking, so the next edit can't use a stale row position.
    if (!ok) {
      removal?.cancel();
      this.#announce("Failed");
      await this.#rerenderSection();
    }

    this.#isUpdating = false;
    // A discount that landed mid-update isn't in the morphed HTML. Re-render with the rows still
    // locked, since the discount may have re-split lines; the re-render unlocks them.
    if (this.#refreshSkippedDuringUpdate) {
      this.#refreshSkippedDuringUpdate = false;
      this.#queueRerender();
    } else {
      this.setLoading(false);
    }
    return ok;
  }

  async removeItem(cartItem) {
    const line = parseInt(cartItem.dataset.line, 10);
    if (isNaN(line)) return;

    // The heading stays on screen when the cart empties, so it catches focus when no row is left.
    const removal = startRowRemoval(cartItem, {
      focusFallback: this.wrapper.querySelector("[data-cart-heading]"),
    });
    await this.updateQuantity(line, 0, { title: cartItem.dataset.itemTitle, removal });
  }

  setLoading(isLoading) {
    if (!this.sectionEl) return;
    this.sectionEl.setAttribute("aria-busy", isLoading.toString());
    setRowsLocked(this.wrapper.querySelectorAll("[data-cart-item]"), isLoading);
  }

  /**
   * @param {"Updated"|"Removed"|"Failed"} key
   * @param {Record<string, string|number>} [values]
   */
  #announce(key, values) {
    announceCartStatus(this.wrapper?.querySelector("[data-cart-aria-live]"), key, values);
  }

  updateCartBadges(count) {
    for (const el of document.querySelectorAll("[data-cart-count]")) {
      el.textContent = count ?? 0;
    }
  }

  updateCartState(data) {
    if (!this.sectionEl) return;
    this.sectionEl.dataset.hasItems = (data.item_count > 0).toString();
    this.sectionEl.dataset.hasDiscounts = ((data.cart_level_discount_applications?.length ?? 0) > 0).toString();
  }

  updateDiscounts(discounts) {
    const el = this.sectionEl?.querySelector("[data-cart-discounts]");
    if (!el) return;

    if (!discounts?.length) {
      el.classList.add("hidden");
      el.parentElement?.classList.add("hidden");
      return;
    }

    el.classList.remove("hidden");
    el.parentElement?.classList.remove("hidden");
    el.innerHTML = "";

    for (const d of discounts) {
      const li = document.createElement("li");
      li.className =
        "relative flex items-center gap-1 before:absolute before:-left-2 before:top-1/2 before:-translate-y-1/2 before:content-['-']";

      const amount = document.createElement("span");
      amount.className = "leading-none";
      amount.textContent = formatMoney(Number(d.total_allocated_amount) || 0);

      li.append(amount);
      el.append(li);
    }
  }

  updateShipping(subtotalPrice) {
    const el = this.sectionEl?.querySelector("[data-cart-shipping]");
    if (!el) return;

    const raw = el.dataset.freeShippingThreshold;
    const threshold = raw ? Number(raw) : NaN;
    if (Number.isNaN(threshold)) {
      el.textContent = "TBD";
    } else {
      el.textContent = subtotalPrice >= threshold ? "FREE" : "TBD";
    }
  }

  updateFinancing(totalPrice) {
    const affirmEl = this.wrapper?.querySelector(".affirm-as-low-as");
    if (affirmEl) {
      affirmEl.dataset.amount = totalPrice;
      const wrapper = affirmEl.closest("[data-affirm-wrapper]");
      if (wrapper && window.affirm) wrapper.hidden = false;
      window.affirm?.ui?.refresh?.();
    }

    const afterpayEl = this.wrapper?.querySelector("square-placement");
    if (afterpayEl) {
      afterpayEl.dataset.amount = (totalPrice / 100).toFixed(2);
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new CartPage());
} else {
  new CartPage();
}
